/**
 * @file SlotLayoutModeResolver.ts
 * @role Chooses the slot presentation layout mode from the current LayoutContext using
 *       safe-frame dimensions plus validated phone/tablet aspect thresholds.
 * @owns SlotLayoutMode, LayoutTemplate, and their deterministic selection policy.
 * @reads LayoutContext only.
 * @neverDoes Position individual controls. Touch Pixi. Read window.
 * @invariants 14.
 * @see docs/UI_LAYOUT_GUIDE.md
 */
import type { LayoutContext } from './LayoutContext';
import type { LayoutPolicyConfig } from '@core/config/UiConfig';

export enum LayoutMode {
  Desktop = 'desktop',
  MobileLandscape = 'mobileLandscape',
  MobilePortrait = 'mobilePortrait',
}

export type SlotLayoutMode = LayoutMode;
export type LayoutTemplate = 'stacked' | 'sideRail';

export const DEFAULT_LAYOUT_BREAKPOINTS: Pick<
  LayoutPolicyConfig,
  'mobileBreakpoint' | 'tabletBreakpoint' | 'tabletLandscapeMaxAspectRatio'
> = {
  mobileBreakpoint: 600,
  tabletBreakpoint: 1100,
  tabletLandscapeMaxAspectRatio: 1.6,
};

export class SlotLayoutModeResolver {
  static resolve(
    layout: LayoutContext,
    breakpoints: Pick<
      LayoutPolicyConfig,
      'mobileBreakpoint' | 'tabletBreakpoint' | 'tabletLandscapeMaxAspectRatio'
    > = DEFAULT_LAYOUT_BREAKPOINTS,
  ): SlotLayoutMode {
    const { width, height } = layout.safeBounds;
    const portrait = width < height;
    const isMobileSize = Math.min(width, height) < breakpoints.mobileBreakpoint;
    if (isMobileSize) return portrait ? LayoutMode.MobilePortrait : LayoutMode.MobileLandscape;
    if (portrait && width < breakpoints.tabletBreakpoint) return LayoutMode.MobilePortrait;
    const aspect = width / Math.max(1, height);
    if (
      width > height &&
      height < breakpoints.tabletBreakpoint &&
      aspect < breakpoints.tabletLandscapeMaxAspectRatio
    ) {
      return LayoutMode.MobileLandscape;
    }
    return LayoutMode.Desktop;
  }
}

/** Mobile landscape uses a side rail; portrait and desktop use bottom bands. */
export function templateForMode(mode: SlotLayoutMode): LayoutTemplate {
  return mode === LayoutMode.MobileLandscape ? 'sideRail' : 'stacked';
}
